import React, { useState } from 'react';
import { useUserInfo } from '../contexts/UserInfoContext';
import { useInvoiceData } from '../hooks/useInvoiceData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity } from '@fortawesome/free-solid-svg-icons';
import { STUDENT_ITAKU_KBN_ID_US, KYOTEN_ID_ONLINE_KAKYO, SHIHARAI_KBN_ID_CREDIT_NAME } from '../constants/constants';

const Invoice: React.FC = () => {
    const { userInfo } = useUserInfo();
    const [activeTab, setActiveTab] = useState(0);
    const studentId = userInfo?.id;
    const kaishaId = userInfo?.kaishaId ?? 0;
    const seikyuMonth = new Date().toISOString().slice(0, 7);
    const token = userInfo?.token ?? '';

    const { invoiceProps, months, fetchInvoiceData } = useInvoiceData(
        studentId,
        kaishaId,
        seikyuMonth,
        token
    );

    const handleTabClick = (
        index: number,
        month: { display: string; value: string }
    ) => {
        setActiveTab(index);
        fetchInvoiceData(month.value);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    if (!invoiceProps) {
        return <div>Loading...</div>;
    }

    const { printItems, kaishaKozaInfo } = invoiceProps;
    const currency = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? '＄' : '円';

    const renderTableRow = (
        shohinmei: string | null,
        tanka: number,
        waribiki: number,
        uneihi: number,
        ieStudy: number,
        kingaku: number
    ) => {
        if (shohinmei && shohinmei !== '') {
            const tankaDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${tanka}` : `${tanka}${currency}`;
            const waribikiDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${waribiki}` : `${waribiki}${currency}`;
            const uneihiDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${uneihi}` : `${uneihi}${currency}`;
            const ieStudyDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${ieStudy}` : `${ieStudy}${currency}`;
            const kingakuDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${kingaku}` : `${kingaku}${currency}`;
            return (
                <tr className="border-b hover:bg-gray-100 transition duration-300 ease-in-out">
                    <td className="p-2">{shohinmei}</td>
                    <td className="p-2">
                        授業料：{tankaDisplay} 割引額：{waribikiDisplay} 維持管理費：{uneihiDisplay} {userInfo?.kyotenId === KYOTEN_ID_ONLINE_KAKYO ? 'OL管理費' : 'EL/OL'}：{ieStudyDisplay}
                    </td>
                    <td className="p-2 text-right">{kingakuDisplay}</td>
                </tr>
            );
        }
        return null;
    }

    const renderSimpleTableRow = (shohinmei: string | null, kingaku: number) => {
        if (shohinmei != null && shohinmei !== '') {
            const kingakuDisplay = userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${kingaku}` : `${kingaku}${currency}`;
            return (
                <tr className="border-b hover:bg-gray-100 transition duration-300 ease-in-out">
                    <td className="p-2">{shohinmei}</td>
                    <td className="p-2"></td>
                    <td className="p-2 text-right">{kingakuDisplay}</td>
                </tr>
            );
        }
        return null;
    };

    return (
        <div className="relative bg-white p-6 max-w-2xl mx-auto w-full">
            <div className="flex justify-between items-center mb-4 bg-gradient-to-r from-green-900 via-green-500 to-teal-500 p-4 rounded-lg shadow-lg">
                <div className="flex items-center">
                    {userInfo?.kyotenId === KYOTEN_ID_ONLINE_KAKYO ? (
                        <img src="/images/online_sem_wide03.jpg" alt="logo" className="w-140 h-12 mr-4 object-contain" />
                    ) : (
                    <img src="/images/wamlogo_wide.jpg" alt="logo" className="w-140 h-12 mr-4 object-contain" />
                    )}
                </div>
                <button
                    onClick={handleLogout}
                    className="bg-red-600 hover:bg-red-800 text-white font-bold py-2 px-4 rounded-full shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                >
                    ログアウト
                </button>
            </div>
            <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400 w-full">
                {months.map((month, index) => (
                    <li key={index} className="flex-1">
                        <button
                            aria-current="page"
                            className={`${
                                activeTab === index
                                    ? 'inline-block p-4 text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-green-950 dark:text-white'
                                    : 'inline-block p-4 rounded-t-lg hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                            } w-full`}
                            onClick={() => handleTabClick(index, month)}
                        >
                            {month.display}
                        </button>
                    </li>
                ))}
            </ul>
            <h1 className="text-2xl font-bold bg-green-900 text-white mb-4 p-2 rounded-lg shadow-md">
                請求情報
            </h1>

            {printItems ? (
                <div>
                    <p className="mb-4 text-sm">
                        当社の学習指導サービスをご利用いただきありがとうございます。
                        今月の「ご請求金額」と「お支払い日」を下記の通りご案内いたします。
                    </p>

                    <div className="bg-gray-100 p-4 rounded-lg mb-4">
                        <div className="grid grid-cols-2 gap-2">
                            <h2 className="bg-black text-white font-bold mb-2 p-2 rounded-lg shadow-lg">
                                お支払い方法
                            </h2>
                            <p className="text-gray-700 font-medium">
                                {printItems.shiharaiKbnName}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-4">
                            <p className="bg-black text-white font-bold p-2 rounded-lg shadow-lg">
                                お支払い日
                            </p>
                            <p className="text-gray-700 font-medium">
                                {printItems.seikyuDate}
                            </p>
                        </div>
                    </div>

                    <p className="mb-4 text-sm">
                    ※下記の「前月までのご請求残額(1)」は、{printItems.registDate}時点のものです。お支払済みの場合は行き違いですので何卒ご容赦ください。
                    {printItems.shiharaiKbnName === SHIHARAI_KBN_ID_CREDIT_NAME && (
                        <div><span>※支払方法：クレジットのご会員様は、決済時の金額が表示されているご請求額と異なる場合があります。</span></div>
                    )}
                    </p>

                    <div className="p-4 rounded-lg mb-4 bg-white shadow-lg">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gradient-to-r from-green-900 to-green-700 text-white p-2 rounded-lg shadow-md">
                                前月までのご請求残額(1)
                            </div>
                            <div className="text-right text-gray-700 font-semibold p-2">
                                {userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${printItems.seikyuZan.toLocaleString()}` : `${printItems.seikyuZan.toLocaleString()}${currency}`}
                            </div>
                            <div className="bg-gradient-to-r from-green-900 to-green-700 text-white p-2 rounded-lg shadow-md">
                                今月のご請求金額(2)
                            </div>
                            <div className="text-right text-gray-700 font-semibold p-2">
                                {userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${printItems.kongetsuGaku.toLocaleString()}` : `${printItems.kongetsuGaku.toLocaleString()}${currency}`}
                            </div>
                            <div className="bg-gradient-to-r from-green-900 to-green-700 text-white p-2 rounded-lg shadow-md">
                                今月までのお預かり金額(3)
                            </div>
                            <div className="text-right text-gray-700 font-semibold p-2">
                                {userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${printItems.pool.toLocaleString()}` : `${printItems.pool.toLocaleString()}${currency}`}
                            </div>
                            <div className="bg-gradient-to-r from-green-900 to-green-700 text-white p-2 rounded-lg shadow-md">
                                今月のお支払い金額((1)+(2))-(3)
                            </div>
                            <div className="text-right text-gray-700 font-semibold p-2">
                                {userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${printItems.seikyuGaku.toLocaleString()}` : `${printItems.seikyuGaku.toLocaleString()}${currency}`}
                            </div>
                        </div>
                    </div>

                    <h2 className="font-bold mb-2 text-xl text-indigo-600">
                        今月分のご請求金額 (2) の明細
                    </h2>
                    <table className="w-full mb-4 border-collapse rounded-lg shadow-lg overflow-hidden">
                        <thead>
                            <tr className="bg-gradient-to-r from-green-500 to-teal-500 text-white">
                                <th className="p-2 text-left">品名</th>
                                <th className="p-2 text-center">備考</th>
                                <th className="p-2 text-right">金額({currency})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTableRow(printItems.shohinmei1, printItems.tanka1, printItems.waribiki1, printItems.uneihi1, printItems.ieStudy1, printItems.kingaku1)}
                            {renderTableRow(printItems.shohinmei2, printItems.tanka2, printItems.waribiki2, printItems.uneihi2, printItems.ieStudy2, printItems.kingaku2)}
                            {renderTableRow(printItems.shohinmei3, printItems.tanka3, printItems.waribiki3, printItems.uneihi3, printItems.ieStudy3, printItems.kingaku3)}
                            {renderTableRow(printItems.shohinmei4, printItems.tanka4, printItems.waribiki4, printItems.uneihi4, printItems.ieStudy4, printItems.kingaku4)}
                            {renderTableRow(printItems.shohinmei5, printItems.tanka5, printItems.waribiki5, printItems.uneihi5, printItems.ieStudy5, printItems.kingaku5)}
                            <tr className="border-b bg-green-50 hover:bg-green-100 transition duration-300 ease-in-out">
                                <td className="p-2">(その他商品内訳)</td>
                                <td className="p-2"></td>
                                <td className="p-2 text-right"></td>
                            </tr>
                            {renderSimpleTableRow(printItems.shohinmei6, printItems.kingaku6)}
                            {renderSimpleTableRow(printItems.shohinmei7, printItems.kingaku7)}
                            {renderSimpleTableRow(printItems.shohinmei8, printItems.kingaku8)}
                            {renderSimpleTableRow(printItems.shohinmei9, printItems.kingaku9)}
                            {renderSimpleTableRow(printItems.shohinmei10, printItems.kingaku10)}
                            {renderSimpleTableRow(printItems.shohinmei11, printItems.kingaku11)}
                            {renderSimpleTableRow(printItems.shohinmei12, printItems.kingaku12)}
                            <tr className="bg-gradient-to-r from-green-900 to-green-700 text-white rounded-lg shadow-lg">
                                <td className="p-2">今月のご請求金額合計</td>
                                <td className="p-2"></td>
                                <td className="p-2 text-right">
                                    {userInfo?.studentItakuKbnId === STUDENT_ITAKU_KBN_ID_US ? `${currency}${printItems.kongetsuGaku.toLocaleString()}` : `${printItems.kongetsuGaku.toLocaleString()}${currency}`}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {kaishaKozaInfo.mywamDispFlg && (
                        <>
                            <p className="mb-4 text-sm">
                                お支払方法がお振込みのご会員様は、お近くのATMより下記の口座にお振込みください。
                            </p>
                            <p className="mb-4 text-sm">
                                お振込みいただく際の振込人名義は「<span className='text-red-500'>お客様番号 生徒名</span>」でお願い致します。
                            </p>

                            <div className="mt-4 text-sm bg-gray-100 p-4 rounded-lg shadow-lg">
                                <div className="flex justify-start items-center">
                                    <FontAwesomeIcon icon={faUniversity} className="mr-2" />
                                    <span className="text-gray-700 font-medium">お客様の振込人名義</span>
                                </div>
                                <span className="font-bold text-red-600">{printItems.kokyakuNo} {printItems.kana}</span>
                            </div>

                            <div className="mt-4 text-sm">
                                <h2 className="font-bold mb-2 text-gray-800">
                                    振込先口座情報
                                </h2>
                                <table className="w-full mb-4 border-collapse rounded-lg shadow-lg overflow-hidden">
                                    <thead>
                                        <tr className="bg-gray-50 text-blue-900">
                                            <th className="p-2 text-center">銀行名</th>
                                            <th className="p-2 text-center">支店名</th>
                                            <th className="p-2 text-center">口座番号</th>
                                            <th className="p-2 text-center">名義</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b">
                                            <td className="p-2">
                                                {kaishaKozaInfo.ginkoName}銀行
                                            </td>
                                            <td className="p-2">
                                                {kaishaKozaInfo.shitenName}
                                            </td>
                                            <td className="p-2">
                                                {kaishaKozaInfo.ginkoKoza}
                                            </td>
                                            <td className="p-2">
                                                {kaishaKozaInfo.ginkoKozaName}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p>※振込手数料はお客様のご負担でお願いいたします。</p>
                            </div>
                        </>
                )}
                </div>
            ) : (
                <div>請求情報はありません。</div>
            )}
        </div>
    );
};

export default Invoice;
